/**
 * Streaming realtime Newton–Euler inverse-dynamics estimator (spec steps 2–7).
 *
 * TypeScript twin of `acro_movement/biomech/estimator.py`. Offline Python uses a zero-phase
 * Butterworth filter and exact central differences over the whole track; this realtime
 * version uses a one-euro filter and a 3-sample ring buffer, so forces/moments agree to a
 * few percent, not bit-exactly (the parity test tolerance is 5%). Contact model and validation
 * follow acro_movement/src/acro_movement/biomech/PORTING_NOTES.md (2026-09-21); streaming-only
 * differences are documented at `detectContacts`' section header and on `validateFrames`.
 * Not ported (offline-only): the Butterworth short-run NaN rule (filters.py) and the per-track
 * image placement scale (normalize.py) — the live filter is a causal one-euro filter.
 *
 * Pure numbers in, pure JSON-shaped objects out — no three.js, no DOM, so it runs under
 * `node --experimental-strip-types`.
 */
import {
  AIRBORNE_PENALTY,
  AIRBORNE_TOLERANCE,
  CONTACT_CAP_PENALTY,
  CONTACT_SEGMENTS,
  CONTACT_WEIGHT_EPS_M,
  DISTRIBUTION_PASSES,
  EDGE_FRAMES,
  FLOOR_PENETRATION_M,
  FRICTION_MU,
  FRICTION_PENALTY,
  GRAVITY,
  GROUND_CONTACT_SPEED_MS,
  GROUND_SEGMENTS,
  JOINTS,
  LOW_CONFIDENCE,
  LOW_CONFIDENCE_SCORE,
  MAX_FINDINGS,
  OVERLOAD_PENALTY,
  PASS_SCORE,
  PENETRATION_PENALTY,
  PERSON_CONTACT_DISTANCE_M,
  PERSON_DEDUPE_M,
  PERSON_FORCE_CAP_BW,
  REQUIRED_POINTS,
  RESIDUAL_ERROR_BW,
  RESIDUAL_FINDING_BW,
  RESIDUAL_FORCE_FREE_BW,
  RESIDUAL_FORCE_PENALTY,
  RESIDUAL_MOMENT_ERROR_BWH,
  RESIDUAL_MOMENT_FINDING_BWH,
  RESIDUAL_MOMENT_FREE_BWH,
  RESIDUAL_MOMENT_PENALTY,
  SEGMENTS,
  SEGMENT_SEARCH_ORDER,
  SEGMENT_TIE_M,
  SUSTAIN_WINDOW_S,
  TRUNK_FLOOR_OFFSET_M,
  WARN_SCORE,
  jointLoad,
  resolveMass,
  type SegmentDef,
  type SegmentEnd,
} from "./constants.ts";
import {
  add,
  addTo,
  centralDerivative,
  clone,
  cross,
  dist,
  distXZ,
  len,
  mid,
  mul,
  normalize,
  pointSegmentDistance,
  secondDerivative,
  sub,
} from "./vec.ts";
import type {
  BiomechContact,
  BiomechFrame,
  BiomechFramePerson,
  BiomechJoint,
  CanonicalPoint,
  EstimatorOptions,
  EstimatorPersonInput,
  JointId,
  PointMap,
  SegmentId,
  UvMap,
  ValidationFinding,
  ValidationSummary,
  Vec2,
  Vec3,
} from "./types.ts";

/* ------------------------------------------------------------ one-euro */

class OneEuroScalar {
  private xPrev = 0;
  private dxPrev = 0;
  private tPrev = 0;
  private started = false;
  private readonly minCutoff: number;
  private readonly beta: number;
  private readonly dCutoff: number;

  // Explicit fields (no parameter properties) so node's strip-only TS mode can load this file.
  constructor(minCutoff: number, beta: number, dCutoff: number) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
  }

  filter(t: number, x: number): number {
    if (!this.started) {
      this.started = true;
      this.tPrev = t;
      this.xPrev = x;
      this.dxPrev = 0;
      return x;
    }
    const dt = Math.max(1e-4, t - this.tPrev);
    this.tPrev = t;
    const dx = (x - this.xPrev) / dt;
    const dxHat = lowPass(dx, this.dxPrev, alpha(dt, this.dCutoff));
    this.dxPrev = dxHat;
    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const xHat = lowPass(x, this.xPrev, alpha(dt, cutoff));
    this.xPrev = xHat;
    return xHat;
  }
}

function alpha(dt: number, cutoff: number): number {
  const tau = 1 / (2 * Math.PI * Math.max(1e-6, cutoff));
  return 1 / (1 + tau / dt);
}

function lowPass(x: number, prev: number, a: number): number {
  return a * x + (1 - a) * prev;
}

class OneEuroVec3 {
  private readonly f: OneEuroScalar[];
  constructor(minCutoff: number, beta: number, dCutoff: number) {
    this.f = [
      new OneEuroScalar(minCutoff, beta, dCutoff),
      new OneEuroScalar(minCutoff, beta, dCutoff),
      new OneEuroScalar(minCutoff, beta, dCutoff),
    ];
  }
  filter(t: number, p: Vec3): Vec3 {
    return [this.f[0]!.filter(t, p[0]), this.f[1]!.filter(t, p[1]), this.f[2]!.filter(t, p[2])];
  }
}

/* ------------------------------------------------------- derived points */

/** mid_hip / mid_shoulder or a plain landmark. */
export function resolveEnd(points: PointMap, end: SegmentEnd): Vec3 | null {
  if (end === "mid_hip") {
    const l = points.left_hip;
    const r = points.right_hip;
    return l && r ? mid(l, r) : (l ?? r ?? null);
  }
  if (end === "mid_shoulder") {
    const l = points.left_shoulder;
    const r = points.right_shoulder;
    return l && r ? mid(l, r) : (l ?? r ?? null);
  }
  if (end === "head_top") {
    const neck = resolveEnd(points, "mid_shoulder");
    const nose = points.nose;
    if (!neck || !nose) return null;
    return add(neck, mul(sub(nose, neck), 1.6));
  }
  if (end === "foot_l" || end === "foot_r") {
    const side = end === "foot_l" ? "left" : "right";
    const toe = points[`${side}_foot_index` as CanonicalPoint];
    const heel = points[`${side}_heel` as CanonicalPoint];
    if (toe && heel) return add(mul(toe, 0.75), mul(heel, 0.25));
    return toe ?? null;
  }
  return points[end] ?? null;
}

/** Landmark whose uv projects a segment end (derived ends use their dominant landmark). */
function uvKeyOf(end: SegmentEnd): CanonicalPoint | null {
  if (end === "mid_hip" || end === "mid_shoulder") return null;
  if (end === "head_top") return "nose";
  if (end === "foot_l") return "left_foot_index";
  if (end === "foot_r") return "right_foot_index";
  return end;
}

/** Image position of a segment end; mid_* ends average their left/right landmarks. */
export function uvOfEnd(uv: UvMap, end: SegmentEnd): Vec2 | null {
  if (end === "mid_hip" || end === "mid_shoulder") {
    const side = end === "mid_hip" ? "hip" : "shoulder";
    const l = uv[`left_${side}` as CanonicalPoint];
    const r = uv[`right_${side}` as CanonicalPoint];
    if (l && r) return [(l[0] + r[0]) / 2, (l[1] + r[1]) / 2];
    return l ?? r ?? null;
  }
  const key = uvKeyOf(end);
  return key ? uv[key] ?? null : null;
}

/** Least-squares affine map world (x, y, z, 1) → image (u, v). */
export type UvProjector = (p: Vec3) => Vec2;

/**
 * Fit u = a·[x y z 1], v = b·[x y z 1] over every landmark that has both a world position and
 * an image position. Needs ≥ 4 non-coplanar points; returns null otherwise (caller hides what
 * it cannot place rather than guessing an orthographic projection).
 */
export function fitUvProjector(points: PointMap, uv: UvMap): UvProjector | null {
  const rows: { p: Vec3; uv: Vec2 }[] = [];
  for (const [key, p] of Object.entries(points)) {
    const q = uv[key as CanonicalPoint];
    if (!p || !q || !Number.isFinite(q[0]) || !Number.isFinite(q[1])) continue;
    rows.push({ p, uv: q });
  }
  if (rows.length < 4) return null;
  // Normal equations AᵀA x = Aᵀb for both coordinates (4×4, shared matrix).
  const ata = [
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ];
  const atu = [0, 0, 0, 0];
  const atv = [0, 0, 0, 0];
  for (const row of rows) {
    const a = [row.p[0], row.p[1], row.p[2], 1];
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) ata[i]![j]! += a[i]! * a[j]!;
      atu[i]! += a[i]! * row.uv[0];
      atv[i]! += a[i]! * row.uv[1];
    }
  }
  const cu = solve4(ata, atu);
  const cv = solve4(ata, atv);
  if (!cu || !cv) return null;
  return (p: Vec3): Vec2 => [
    cu[0]! * p[0] + cu[1]! * p[1] + cu[2]! * p[2] + cu[3]!,
    cv[0]! * p[0] + cv[1]! * p[1] + cv[2]! * p[2] + cv[3]!,
  ];
}

/** Gaussian elimination with partial pivoting; null when (near-)singular. */
function solve4(matrix: number[][], rhs: number[]): number[] | null {
  const n = 4;
  const m = matrix.map((row, i) => [...row, rhs[i]!]);
  let scale = 0;
  for (const row of m) for (let j = 0; j < n; j++) scale = Math.max(scale, Math.abs(row[j]!));
  if (scale === 0) return null;
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(m[r]![col]!) > Math.abs(m[pivot]![col]!)) pivot = r;
    }
    if (Math.abs(m[pivot]![col]!) < scale * 1e-9) return null;
    [m[col], m[pivot]] = [m[pivot]!, m[col]!];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = m[r]![col]! / m[col]![col]!;
      if (f === 0) continue;
      for (let c = col; c <= n; c++) m[r]![c]! -= f * m[col]![c]!;
    }
  }
  const out = m.map((row, i) => row[n]! / row[i]!);
  return out.every(Number.isFinite) ? out : null;
}

/**
 * Fill optional extremities that pose sources often omit (nose, hand index, foot index).
 * Mutates and returns `points`. Extrapolations are crude but only affect small-mass
 * terminal segments.
 */
export function fillDerivedPoints(points: PointMap): PointMap {
  const midShoulder = resolveEnd(points, "mid_shoulder");
  const midHip = resolveEnd(points, "mid_hip");
  if (!points.nose && midShoulder && midHip) {
    const trunk = sub(midShoulder, midHip);
    points.nose = add(midShoulder, mul(trunk, 0.3));
  }
  for (const side of ["left", "right"] as const) {
    const wrist = points[`${side}_wrist` as CanonicalPoint];
    const elbow = points[`${side}_elbow` as CanonicalPoint];
    const idx = points[`${side}_index` as CanonicalPoint];
    if (!idx && wrist && elbow) {
      points[`${side}_index` as CanonicalPoint] = add(wrist, mul(sub(wrist, elbow), 0.34));
    }
    const ankle = points[`${side}_ankle` as CanonicalPoint];
    const heel = points[`${side}_heel` as CanonicalPoint];
    const toe = points[`${side}_foot_index` as CanonicalPoint];
    const knee = points[`${side}_knee` as CanonicalPoint];
    if (!toe && ankle) {
      if (heel) {
        points[`${side}_foot_index` as CanonicalPoint] = add(ankle, mul(sub(ankle, heel), 2));
      } else if (knee) {
        // Foot is roughly perpendicular to the shank, pointing forward and down.
        const shank = normalize(sub(ankle, knee));
        const forward = normalize([-shank[1], 0, 0] as Vec3);
        const fwd = len(forward) > 0 ? forward : ([0, 0, 1] as Vec3);
        points[`${side}_foot_index` as CanonicalPoint] = add(ankle, mul(fwd, 0.16));
      }
    }
    if (!heel && ankle && toe) {
      points[`${side}_heel` as CanonicalPoint] = add(ankle, mul(sub(ankle, toe), 0.4));
    }
  }
  return points;
}

/** Fraction of REQUIRED_POINTS present. */
export function pointCoverage(points: PointMap): number {
  let n = 0;
  for (const key of REQUIRED_POINTS) if (points[key]) n++;
  return n / REQUIRED_POINTS.length;
}

/* ---------------------------------------------------------- internals */

interface PersonSample {
  meta: EstimatorPersonInput;
  points: PointMap;
  uv: UvMap;
}

interface BufferFrame {
  t: number;
  persons: Map<number, PersonSample>;
}

interface SegKin {
  def: SegmentDef;
  mass: number;
  prox: Vec3;
  distal: Vec3;
  com: Vec3;
  acc: Vec3;
  alpha: Vec3;
  inertia: number;
  length: number;
}

interface PersonKin {
  id: number;
  meta: EstimatorPersonInput;
  massKg: number;
  heightM: number;
  points: PointMap;
  uv: UvMap;
  segments: Map<SegmentId, SegKin>;
  com: Vec3;
  comAcc: Vec3;
  totalMass: number;
  coverage: number;
  /** External point loads applied to this person (contacts + other persons). */
  external: ExternalLoad[];
}

interface ExternalLoad {
  p: Vec3;
  f: Vec3;
  /** Segment of this person the load acts on (null → nearest segment). */
  segment: SegmentId | null;
}

export interface EstimatorFrameExtras {
  /** Whole-body COM acceleration per person id (used by validation). */
  comAcc: Map<number, Vec3>;
  coverage: Map<number, number>;
  masses: Map<number, number>;
  heights: Map<number, number>;
  floorY: number;
}

const ZERO: Vec3 = [0, 0, 0];

/** Points whose low percentile tracks the floor (distal contact points, not ankles). */
const FLOOR_POINTS: CanonicalPoint[] = [
  "left_heel",
  "right_heel",
  "left_foot_index",
  "right_foot_index",
  "left_index",
  "right_index",
];
const TRUNK_FLOOR_ENDS: SegmentEnd[] = ["mid_hip", "mid_shoulder"];
/** Rolling floor window (seconds) and the low percentile taken over it. */
const FLOOR_WINDOW_S = 3;
const FLOOR_PERCENTILE = 0.05;
const FLOOR_MAX_SAMPLES = 4000;
/** Heel/toe/finger-tip markers sit ~2 cm above the contact plane. */
const FLOOR_MARKER_OFFSET_M = 0.02;

/* --------------------------------------------------------- estimator */

export class RealtimeEstimator {
  private readonly opts: Required<Omit<EstimatorOptions, "floorY">> & { floorY: number | null };
  private buffer: BufferFrame[] = [];
  private filters = new Map<string, OneEuroVec3>();
  private floorEstimate: number | null = null;
  /** Recent slow distal-point heights (rolling robust floor estimate). */
  private floorSamples: { t: number; y: number }[] = [];
  private floorPrev = new Map<string, { t: number; p: Vec3 }>();
  /** Extras of the most recent solved frame (kinematic context for validation/UI). */
  lastExtras: EstimatorFrameExtras | null = null;

  constructor(opts: EstimatorOptions = {}) {
    this.opts = {
      gravity: opts.gravity ?? (GRAVITY.slice() as Vec3),
      minCutoff: opts.minCutoff ?? 2.5,
      beta: opts.beta ?? 0.7,
      dCutoff: opts.dCutoff ?? 1.0,
      filter: opts.filter !== false,
      autoFloor: opts.autoFloor !== false,
      floorY: opts.floorY ?? null,
    };
  }

  get floorY(): number {
    if (this.opts.floorY != null) return this.opts.floorY;
    return this.floorEstimate ?? 0;
  }

  /** Fix the floor plane (e.g. the scene floor in 3D); null re-enables auto tracking. */
  setFloorY(y: number | null): void {
    this.opts.floorY = y != null && Number.isFinite(y) ? y : null;
  }

  reset(): void {
    this.buffer = [];
    this.filters.clear();
    this.floorEstimate = null;
    this.floorSamples = [];
    this.floorPrev.clear();
    this.lastExtras = null;
  }

  /**
   * Feed one sample. Returns the solved frame for the *middle* of the 3-sample window
   * (one-sample latency, needed for central differences), or null while filling up.
   */
  push(t: number, persons: EstimatorPersonInput[]): BiomechFrame | null {
    const last = this.buffer[this.buffer.length - 1];
    if (last && t <= last.t) {
      // Non-monotonic time (seek / loop): restart the window before filtering the new sample.
      this.buffer = [];
      this.filters.clear();
      this.floorPrev.clear();
      this.floorSamples = [];
    }
    const snap = new Map<number, PersonSample>();
    for (const person of persons ?? []) {
      if (!person || typeof person.id !== "number") continue;
      const pts = this.filterPoints(person, t);
      fillDerivedPoints(pts);
      snap.set(person.id, { meta: person, points: pts, uv: person.uv ?? {} });
      this.trackFloor(person.id, pts, t);
    }
    this.updateFloorEstimate(t);
    this.buffer.push({ t, persons: snap });
    if (this.buffer.length > 3) this.buffer.shift();
    if (this.buffer.length < 3) return null;
    const [f0, f1, f2] = this.buffer as [BufferFrame, BufferFrame, BufferFrame];
    return this.solve(f0, f1, f2);
  }

  /**
   * Quasi-static solve of one pose (zero accelerations): used while time does not advance
   * (paused video / paused 3D scene / pose editing) so the overlay still shows the loads
   * needed to hold the current pose. Does not touch the streaming window or filters.
   */
  solveStatic(t: number, persons: EstimatorPersonInput[]): BiomechFrame | null {
    const snap = new Map<number, PersonSample>();
    for (const person of persons ?? []) {
      if (!person || typeof person.id !== "number") continue;
      const pts: PointMap = {};
      for (const [key, value] of Object.entries(person.points ?? {})) {
        if (!value || value.length < 3) continue;
        const p: Vec3 = [Number(value[0]), Number(value[1]), Number(value[2])];
        if (Number.isFinite(p[0]) && Number.isFinite(p[1]) && Number.isFinite(p[2])) {
          pts[key as CanonicalPoint] = p;
        }
      }
      fillDerivedPoints(pts);
      snap.set(person.id, { meta: person, points: pts, uv: person.uv ?? {} });
      if (this.floorEstimate == null) this.seedFloor(pts);
    }
    if (snap.size === 0) return null;
    // Identical samples one second apart → zero velocity/acceleration everywhere.
    const frame: BufferFrame = { t, persons: snap };
    return this.solve({ t: t - 1, persons: snap }, frame, { t: t + 1, persons: snap });
  }

  private filterPoints(person: EstimatorPersonInput, t: number): PointMap {
    const out: PointMap = {};
    for (const [key, value] of Object.entries(person.points ?? {})) {
      if (!value || value.length < 3) continue;
      const p: Vec3 = [Number(value[0]), Number(value[1]), Number(value[2])];
      if (!Number.isFinite(p[0]) || !Number.isFinite(p[1]) || !Number.isFinite(p[2])) continue;
      if (!this.opts.filter) {
        out[key as CanonicalPoint] = p;
        continue;
      }
      const id = `${person.id}:${key}`;
      let f = this.filters.get(id);
      if (!f) {
        f = new OneEuroVec3(this.opts.minCutoff, this.opts.beta, this.opts.dCutoff);
        this.filters.set(id, f);
      }
      out[key as CanonicalPoint] = f.filter(t, p);
    }
    return out;
  }

  /**
   * Collect slow distal points (feet / finger tips moving slower than the ground-contact
   * speed) — the floor is where resting extremities are, not the single lowest point ever
   * seen, so one bad landmark (or a jump off a box) cannot drag the floor down for good.
   */
  private trackFloor(personId: number, points: PointMap, t: number): void {
    if (!this.opts.autoFloor || this.opts.floorY != null) return;
    if (this.floorEstimate == null) this.seedFloor(points);
    for (const key of FLOOR_POINTS) {
      const p = points[key];
      if (!p) continue;
      const id = `${personId}:${key}`;
      const prev = this.floorPrev.get(id);
      this.floorPrev.set(id, { t, p });
      if (!prev || t <= prev.t) continue;
      const speed = dist(prev.p, p) / (t - prev.t);
      if (speed < GROUND_CONTACT_SPEED_MS) {
        this.floorSamples.push({ t, y: p[1] - FLOOR_MARKER_OFFSET_M });
      }
    }
    // A lying/sitting body rests on its back/pelvis, ~TRUNK_FLOOR_OFFSET_M below the
    // mid_hip/mid_shoulder landmarks (Python normalize._floor_height pools these too), so a
    // supine base with feet and hands in the air still finds the real floor.
    for (const end of TRUNK_FLOOR_ENDS) {
      const p = resolveEnd(points, end);
      if (!p) continue;
      const id = `${personId}:${end}`;
      const prev = this.floorPrev.get(id);
      this.floorPrev.set(id, { t, p });
      if (!prev || t <= prev.t) continue;
      if (dist(prev.p, p) / (t - prev.t) <= GROUND_CONTACT_SPEED_MS) {
        this.floorSamples.push({ t, y: p[1] - TRUNK_FLOOR_OFFSET_M });
      }
    }
    if (this.floorSamples.length > FLOOR_MAX_SAMPLES) {
      this.floorSamples.splice(0, this.floorSamples.length - FLOOR_MAX_SAMPLES);
    }
  }

  /** First guess before any slow samples exist: the lowest foot point of this pose. */
  private seedFloor(points: PointMap): void {
    if (!this.opts.autoFloor || this.opts.floorY != null) return;
    let low = Infinity;
    for (const key of FLOOR_POINTS) {
      const p = points[key];
      if (p) low = Math.min(low, p[1]);
    }
    if (Number.isFinite(low)) this.floorEstimate = low - FLOOR_MARKER_OFFSET_M;
  }

  /** Robust low percentile over the rolling window. */
  private updateFloorEstimate(t: number): void {
    if (!this.opts.autoFloor || this.opts.floorY != null) return;
    const cutoff = t - FLOOR_WINDOW_S;
    let drop = 0;
    while (drop < this.floorSamples.length && this.floorSamples[drop]!.t < cutoff) drop++;
    if (drop > 0) this.floorSamples.splice(0, drop);
    if (this.floorSamples.length === 0) return;
    const ys = this.floorSamples.map((s) => s.y).sort((a, b) => a - b);
    const idx = Math.min(ys.length - 1, Math.floor(FLOOR_PERCENTILE * (ys.length - 1)));
    this.floorEstimate = ys[idx]!;
  }

  /* ------------------------------------------------------------ solve */

  private solve(f0: BufferFrame, f1: BufferFrame, f2: BufferFrame): BiomechFrame | null {
    const dt1 = Math.max(1e-4, f1.t - f0.t);
    const dt2 = Math.max(1e-4, f2.t - f1.t);
    const g = this.opts.gravity;
    const floorY = this.floorY;

    const people: PersonKin[] = [];
    for (const [id, sample] of f1.persons) {
      const s0 = f0.persons.get(id);
      const s2 = f2.persons.get(id);
      if (!s0 || !s2) continue;
      const kin = buildPersonKin(id, sample, s0, s2, dt1, dt2);
      if (kin) people.push(kin);
    }
    if (people.length === 0) return null;

    // --- contacts (step 4)
    const detected = detectContacts(people, f0, f2, dt1, dt2, floorY);

    // --- external load distribution, top-down by COM height (step 5)
    const { residuals, contacts } = distributeLoads(people, detected, g, floorY);

    // --- Newton–Euler inverse dynamics (step 6) + loads (step 7)
    const framePersons: BiomechFramePerson[] = [];
    const projectors = new Map<number, UvProjector | null>();
    for (const person of people) {
      const joints = inverseDynamics(person, g);
      const out: BiomechFramePerson = {
        id: person.id,
        com: clone(person.com),
        joints,
        residual: residuals.get(person.id) ?? { f: clone(ZERO), m: clone(ZERO) },
      };
      // Image-sourced persons: place everything without a landmark (COM, residual, derived
      // joints) through a per-frame affine world→image fit instead of an orthographic guess.
      if (Object.keys(person.uv).length > 0) {
        const project = fitUvProjector(person.points, person.uv);
        projectors.set(person.id, project);
        out.comUv = project ? project(person.com) : null;
        for (const joint of Object.values(joints)) {
          if (joint && !joint.uv && project) joint.uv = project(joint.p);
        }
      }
      framePersons.push(out);
    }
    for (const contact of contacts) {
      const project = projectors.get(contact.personId);
      if (!project) continue;
      // Ground contacts sit at the centre of pressure on the floor, not on the toe landmark.
      if (!contact.uv || contact.kind === "ground") contact.uv = project(contact.p);
    }

    this.lastExtras = {
      comAcc: new Map(people.map((p) => [p.id, p.comAcc])),
      coverage: new Map(people.map((p) => [p.id, p.coverage])),
      masses: new Map(people.map((p) => [p.id, p.massKg])),
      heights: new Map(people.map((p) => [p.id, p.heightM])),
      floorY,
    };

    return { t: f1.t, persons: framePersons, contacts };
  }
}

/* ------------------------------------------------------- kinematics */

function personHeight(points: PointMap): number {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of Object.values(points)) {
    if (!p) continue;
    lo = Math.min(lo, p[1]);
    hi = Math.max(hi, p[1]);
  }
  const h = hi - lo;
  return Number.isFinite(h) && h > 0.5 ? h * 1.05 : 0;
}

function buildPersonKin(
  id: number,
  s1: PersonSample,
  s0: PersonSample,
  s2: PersonSample,
  dt1: number,
  dt2: number,
): PersonKin | null {
  const heightM =
    s1.meta.heightM && s1.meta.heightM > 0.5 ? s1.meta.heightM : personHeight(s1.points) || 0;
  const { massKg } = resolveMass(s1.meta.massKg, heightM || null);

  // Mass fractions of the segments we can actually build are renormalised to massKg so a
  // missing hand does not silently shrink the body weight.
  const active: { def: SegmentDef; prox: Vec3; distal: Vec3; p0: Vec3[]; p2: Vec3[] }[] = [];
  let fractionSum = 0;
  for (const def of SEGMENTS) {
    const prox = resolveEnd(s1.points, def.proximal);
    const distal = resolveEnd(s1.points, def.distal);
    const prox0 = resolveEnd(s0.points, def.proximal);
    const distal0 = resolveEnd(s0.points, def.distal);
    const prox2 = resolveEnd(s2.points, def.proximal);
    const distal2 = resolveEnd(s2.points, def.distal);
    if (!prox || !distal || !prox0 || !distal0 || !prox2 || !distal2) continue;
    active.push({ def, prox, distal, p0: [prox0, distal0], p2: [prox2, distal2] });
    fractionSum += def.massFraction;
  }
  if (fractionSum <= 0.2) return null; // not enough body to do physics with
  const scale = 1 / fractionSum;

  const segments = new Map<SegmentId, SegKin>();
  let comNum: Vec3 = [0, 0, 0];
  let accNum: Vec3 = [0, 0, 0];
  let totalMass = 0;

  for (const entry of active) {
    const def = entry.def;
    const mass = def.massFraction * scale * massKg;
    const length = Math.max(def.minLength, dist(entry.prox, entry.distal));
    const com = comOf(entry.prox, entry.distal, def.comRatio);
    const com0 = comOf(entry.p0[0]!, entry.p0[1]!, def.comRatio);
    const com2 = comOf(entry.p2[0]!, entry.p2[1]!, def.comRatio);
    const acc = secondDerivative(com0, com, com2, dt1, dt2);

    // Angular motion of the segment long axis (axial spin ignored — see spec step 3).
    const u0 = normalize(sub(entry.p0[1]!, entry.p0[0]!));
    const u1 = normalize(sub(entry.distal, entry.prox));
    const u2 = normalize(sub(entry.p2[1]!, entry.p2[0]!));
    const w1 = cross(mid(u0, u1), mul(sub(u1, u0), 1 / dt1));
    const w2 = cross(mid(u1, u2), mul(sub(u2, u1), 1 / dt2));
    const alphaV = mul(sub(w2, w1), 1 / Math.max(1e-4, (dt1 + dt2) / 2));

    const radius = def.gyrationRatio * length;
    segments.set(def.id, {
      def,
      mass,
      prox: entry.prox,
      distal: entry.distal,
      com,
      acc,
      alpha: alphaV,
      inertia: mass * radius * radius,
      length,
    });

    comNum = add(comNum, mul(com, mass));
    accNum = add(accNum, mul(acc, mass));
    totalMass += mass;
  }

  if (totalMass <= 0) return null;
  return {
    id,
    meta: s1.meta,
    massKg,
    heightM,
    points: s1.points,
    uv: s1.uv,
    segments,
    com: mul(comNum, 1 / totalMass),
    comAcc: mul(accNum, 1 / totalMass),
    totalMass,
    coverage: pointCoverage(s1.points),
    external: [],
  };
}

function comOf(prox: Vec3, distal: Vec3, ratio: number): Vec3 {
  return [
    prox[0] + (distal[0] - prox[0]) * ratio,
    prox[1] + (distal[1] - prox[1]) * ratio,
    prox[2] + (distal[2] - prox[2]) * ratio,
  ];
}

/* ---------------------------------------------------------- contacts */

/*
 * Mirrors acro_movement/biomech/contacts.py (see PORTING_NOTES.md):
 * - every segment in GROUND_SEGMENTS can rest on the floor (thickness allowance + speed gate);
 * - a person contact is attached to the SUPPORTED person (higher mid_hip) at their segment
 *   and mirrored onto the supporting person at theirs, whoever owns the touching endpoint;
 * - person contacts are capped at PERSON_FORCE_CAP_BW body weights (excess → residual);
 * - ground centres of pressure / contact weights use the load centre (own COM combined with
 *   the points where supported persons push down).
 * Streaming difference: speeds are central differences over the 3-sample window (Python:
 * np.gradient over the filtered track).
 */

/** Contact under construction; `out` is what lands in the frame. */
interface WorkContact {
  out: BiomechContact;
  /** Segment of `out.personId` the force acts on. */
  segment: SegmentId;
  /** Ground: part [s0, s1] of the segment the centre of pressure may move along. */
  sRange: [number, number] | null;
  /** Person contacts: label / segment / image position on the other (supporting) person. */
  mirrorPoint: string | null;
  mirrorSegment: SegmentId | null;
  mirrorUv: Vec2 | null;
  normalClamped: boolean;
  frictionClamped: boolean;
  forceCapped: boolean;
  /** Dedupe preference: the touching endpoint belongs to the supported person. */
  endpointOwned: boolean;
  distance: number;
}

function endSpeed(
  personId: number,
  end: SegmentEnd,
  f0: BufferFrame,
  f2: BufferFrame,
  dtTotal: number,
): number {
  const s0 = f0.persons.get(personId);
  const s2 = f2.persons.get(personId);
  if (!s0 || !s2) return 0;
  const a = resolveEnd(s0.points, end);
  const b = resolveEnd(s2.points, end);
  return a && b ? len(centralDerivative(a, b, dtTotal)) : 0;
}

/** Part [s0, s1] of a segment whose (linearly interpolated) height is ≤ allowance. */
function heightRange(ha: number, hb: number, allowance: number): [number, number] {
  if (Math.abs(hb - ha) < 1e-9) return [0, 1];
  const sStar = Math.min(1, Math.max(0, (allowance - ha) / (hb - ha)));
  return ha <= hb ? [0, sStar] : [sStar, 1];
}

function groundContacts(
  person: PersonKin,
  f0: BufferFrame,
  f2: BufferFrame,
  dtTotal: number,
  floorY: number,
  out: WorkContact[],
): void {
  for (const spec of GROUND_SEGMENTS) {
    const seg = person.segments.get(spec.segment);
    if (!seg) continue;
    const def = seg.def;
    const ha = seg.prox[1] - floorY;
    const hb = seg.distal[1] - floorY;
    let useB: boolean;
    let h: number;
    switch (spec.mode) {
      case "sole":
        useB = true;
        h = hb;
        break;
      case "proximal":
        useB = false;
        h = ha;
        break;
      case "lowest":
        useB = hb < ha;
        h = useB ? hb : ha;
        break;
      default: // flat
        useB = hb < ha;
        h = Math.max(ha, hb);
        break;
    }
    if (!(h <= spec.allowance)) continue;
    // Speed gate (evaluated only for segments low enough to matter).
    let speed: number;
    if (spec.mode === "flat") {
      speed = Math.max(
        endSpeed(person.id, def.proximal, f0, f2, dtTotal),
        endSpeed(person.id, def.distal, f0, f2, dtTotal),
      );
    } else {
      speed = endSpeed(person.id, useB ? def.distal : def.proximal, f0, f2, dtTotal);
    }
    if (!(speed <= GROUND_CONTACT_SPEED_MS)) continue;

    let sRange: [number, number];
    let label: string;
    let labelEnd: SegmentEnd;
    if (spec.mode === "sole") {
      sRange = [spec.sMin, 1];
      label = def.distalPoint;
      labelEnd = def.distal;
    } else {
      sRange = heightRange(ha, hb, spec.allowance);
      if (spec.sMin < 0 && sRange[0] <= 0) sRange = [spec.sMin, sRange[1]];
      label = useB ? def.distalPoint : def.proximalPoint;
      labelEnd = useB ? def.distal : def.proximal;
    }
    out.push({
      out: {
        personId: person.id,
        otherPersonId: null,
        joint: label,
        kind: "ground",
        p: clone(useB ? seg.distal : seg.prox),
        uv: uvOfEnd(person.uv, labelEnd),
        f: [0, 0, 0],
      },
      segment: def.id,
      sRange,
      mirrorPoint: null,
      mirrorSegment: null,
      mirrorUv: null,
      normalClamped: false,
      frictionClamped: false,
      forceCapped: false,
      endpointOwned: false,
      distance: 0,
    });
  }
}

interface SegmentHit {
  d: number;
  seg: SegKin;
  closest: Vec3;
}

/** Closest segment of `person` to p; SEGMENT_TIE_M tie tolerance favours earlier segments. */
function closestSegment(person: PersonKin, p: Vec3): SegmentHit | null {
  let best: SegmentHit | null = null;
  for (const id of SEGMENT_SEARCH_ORDER) {
    const seg = person.segments.get(id);
    if (!seg) continue;
    const a = seg.prox;
    const b = seg.distal;
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const abz = b[2] - a[2];
    const denom = abx * abx + aby * aby + abz * abz;
    let t = 0;
    if (denom > 1e-12) {
      t = ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby + (p[2] - a[2]) * abz) / denom;
      t = Math.max(0, Math.min(1, t));
    }
    const cx = a[0] + t * abx;
    const cy = a[1] + t * aby;
    const cz = a[2] + t * abz;
    const d = Math.hypot(p[0] - cx, p[1] - cy, p[2] - cz);
    if (!best || d < best.d - SEGMENT_TIE_M) best = { d, seg, closest: [cx, cy, cz] };
  }
  return best;
}

/** End of `seg` nearer to `closest`: [canonical label, SegmentEnd for uv]. */
function nearerEnd(seg: SegKin, closest: Vec3): [string, SegmentEnd] {
  return dist(closest, seg.prox) <= dist(closest, seg.distal)
    ? [seg.def.proximalPoint, seg.def.proximal]
    : [seg.def.distalPoint, seg.def.distal];
}

function personContacts(people: PersonKin[], out: WorkContact[]): void {
  if (people.length < 2) return;
  const hipY = new Map<number, number>();
  for (const p of people) hipY.set(p.id, resolveEnd(p.points, "mid_hip")?.[1] ?? -Infinity);

  const found: WorkContact[] = [];
  for (const person of people) {
    for (const segId of CONTACT_SEGMENTS) {
      const ownSeg = person.segments.get(segId);
      if (!ownSeg) continue;
      const e = ownSeg.distal;
      let best: SegmentHit | null = null;
      let other: PersonKin | null = null;
      for (const candidate of people) {
        if (candidate.id === person.id) continue;
        const hit = closestSegment(candidate, e);
        if (hit && (!best || hit.d < best.d)) {
          best = hit;
          other = candidate;
        }
      }
      if (!best || !other || best.d > PERSON_CONTACT_DISTANCE_M) continue;
      const where: Vec3 = mid(e, best.closest);
      const [otherLabel, otherEnd] = nearerEnd(best.seg, best.closest);
      const ownUv = uvOfEnd(person.uv, ownSeg.def.distal);
      const otherUv = uvOfEnd(other.uv, otherEnd);
      const supportedOwnsEndpoint = (hipY.get(person.id) ?? -Infinity) >= (hipY.get(other.id) ?? -Infinity);
      const base = {
        sRange: null,
        normalClamped: false,
        frictionClamped: false,
        forceCapped: false,
        distance: best.d,
      };
      if (supportedOwnsEndpoint) {
        // Feet on shoulders: the endpoint owner is the supported person.
        found.push({
          ...base,
          out: {
            personId: person.id,
            otherPersonId: other.id,
            joint: ownSeg.def.distalPoint,
            kind: "person",
            p: where,
            uv: ownUv,
            f: [0, 0, 0],
          },
          segment: segId,
          mirrorPoint: otherLabel,
          mirrorSegment: best.seg.def.id,
          mirrorUv: otherUv,
          endpointOwned: true,
        });
      } else {
        // L-base feet under the flyer's hips: attach to the supported (other) person.
        found.push({
          ...base,
          out: {
            personId: other.id,
            otherPersonId: person.id,
            joint: otherLabel,
            kind: "person",
            p: where,
            uv: otherUv,
            f: [0, 0, 0],
          },
          segment: best.seg.def.id,
          mirrorPoint: ownSeg.def.distalPoint,
          mirrorSegment: segId,
          mirrorUv: ownUv,
          endpointOwned: false,
        });
      }
    }
  }
  if (found.length === 0) return;

  // Dedupe: one contact per physical touch of the same (supported, supporting) pair;
  // prefer detections whose endpoint belongs to the supported person, then the closer one.
  const sorted = found
    .map((c, i) => ({ c, i }))
    .sort(
      (a, b) =>
        Number(!a.c.endpointOwned) - Number(!b.c.endpointOwned) ||
        a.c.distance - b.c.distance ||
        a.i - b.i,
    );
  const kept: WorkContact[] = [];
  for (const { c } of sorted) {
    const duplicate = kept.some(
      (k) =>
        k.out.personId === c.out.personId &&
        k.out.otherPersonId === c.out.otherPersonId &&
        dist(k.out.p, c.out.p) <= PERSON_DEDUPE_M,
    );
    if (!duplicate) kept.push(c);
  }
  const keep = new Set(kept);
  for (const c of found) if (keep.has(c)) out.push(c);
}

function detectContacts(
  people: PersonKin[],
  f0: BufferFrame,
  f2: BufferFrame,
  dt1: number,
  dt2: number,
  floorY: number,
): WorkContact[] {
  const dtTotal = dt1 + dt2;
  const contacts: WorkContact[] = [];
  for (const person of people) groundContacts(person, f0, f2, dtTotal, floorY, contacts);
  personContacts(people, contacts);
  return contacts;
}

/**
 * Where a person's total downward load acts horizontally: the own COM (weight `weightN`)
 * combined with the points where supported persons push down on them (y kept at the COM).
 */
function loadCentre(com: Vec3, weightN: number, received: ExternalLoad[]): Vec3 {
  let total = Math.max(weightN, 0);
  let x = com[0] * total;
  let z = com[2] * total;
  for (const r of received) {
    const down = -r.f[1];
    if (down > 0) {
      x += down * r.p[0];
      z += down * r.p[2];
      total += down;
    }
  }
  if (total <= 0) return com;
  return [x / total, com[1], z / total];
}

/**
 * Ground application point: the point of the contact's allowed segment range horizontally
 * closest to the load centre, projected onto the floor.
 */
function centreOfPressure(person: PersonKin, c: WorkContact, centre: Vec3, floorY: number): Vec3 {
  const seg = person.segments.get(c.segment);
  const p = c.out.p;
  if (!seg || !Number.isFinite(centre[0]) || !Number.isFinite(centre[2])) {
    return [p[0], Math.min(p[1], floorY), p[2]];
  }
  const a = seg.prox;
  const b = seg.distal;
  const [lo, hi] = c.sRange ?? [0, 1];
  const abx = b[0] - a[0];
  const abz = b[2] - a[2];
  const denom = abx * abx + abz * abz;
  let s = hi;
  if (denom > 1e-12) {
    s = ((centre[0] - a[0]) * abx + (centre[2] - a[2]) * abz) / denom;
    s = Math.min(hi, Math.max(lo, s));
  }
  return [a[0] + s * (b[0] - a[0]), floorY, a[2] + s * (b[2] - a[2])];
}

function clampGround(c: WorkContact): void {
  const f = c.out.f;
  if (f[1] < 0) {
    f[1] = 0;
    c.normalClamped = true;
  }
  const ft = Math.hypot(f[0], f[2]);
  const limit = FRICTION_MU * f[1];
  if (ft > limit + 1e-9) {
    const s = ft > 0 ? limit / ft : 0;
    f[0] *= s;
    f[2] *= s;
    c.frictionClamped = true;
  }
}

function capPerson(c: WorkContact, capN: number): void {
  const mag = len(c.out.f);
  if (mag > capN && capN > 0) {
    const s = capN / mag;
    c.out.f = [c.out.f[0] * s, c.out.f[1] * s, c.out.f[2] * s];
    c.forceCapped = true;
  }
}

/* ------------------------------------------- external load distribution */

/**
 * Spec step 5. Persons are processed top-down by COM height; each person's required force
 * (minus what persons above already push onto them) is split over their contacts with weights
 * ∝ 1/(horizontal distance to the load centre + 5 cm). Ground contacts are clamped (Fy ≥ 0,
 * μ = 0.8), person contacts capped at 3 BW; the unexplained remainder is re-offered to the
 * still-free contacts for a few passes and what is left is the residual. Person contacts push
 * the equal-and-opposite force (listed as a mirrored contact) onto the supporting person.
 */
function distributeLoads(
  people: PersonKin[],
  contacts: WorkContact[],
  g: Vec3,
  floorY: number,
): { residuals: Map<number, { f: Vec3; m: Vec3 }>; contacts: BiomechContact[] } {
  const order = [...people].sort((a, b) => b.com[1] - a.com[1]); // top-down
  const byId = new Map(people.map((p) => [p.id, p]));
  const residualF = new Map<number, Vec3>();
  const processed = new Set<number>();
  const received = new Map<number, ExternalLoad[]>();
  const mirrored: BiomechContact[] = [];
  const gMag = len(g);

  for (const person of order) {
    const need = mul(sub(person.comAcc, g), person.totalMass);
    for (const ext of person.external) addTo(need, mul(ext.f, -1));

    const own = contacts.filter((c) => c.out.personId === person.id);
    if (own.length === 0) {
      residualF.set(person.id, need);
      processed.add(person.id);
      continue;
    }

    const centre = loadCentre(person.com, person.massKg * gMag, received.get(person.id) ?? []);
    for (const c of own) {
      if (c.out.kind === "ground") c.out.p = centreOfPressure(person, c, centre, floorY);
    }
    let weights = own.map((c) => 1 / (distXZ(c.out.p, centre) + CONTACT_WEIGHT_EPS_M));
    if (!weights.every(Number.isFinite)) weights = own.map(() => 1);
    const wTotal = weights.reduce((a, b) => a + b, 0) || 1;
    weights = weights.map((w) => w / wTotal);

    const capN = PERSON_FORCE_CAP_BW * person.massKg * gMag;
    for (const c of own) {
      c.out.f = [0, 0, 0];
      c.normalClamped = false;
      c.frictionClamped = false;
      c.forceCapped = false;
    }
    let remaining = clone(need);
    let free = own.map((_, i) => i);
    for (let pass = 0; pass < DISTRIBUTION_PASSES; pass++) {
      if (free.length === 0 || len(remaining) < 1e-9) break;
      let wSum = 0;
      for (const i of free) wSum += weights[i]!;
      const stillFree: number[] = [];
      for (const i of free) {
        const c = own[i]!;
        const share = wSum > 0 ? weights[i]! / wSum : 1 / free.length;
        c.out.f = [
          c.out.f[0] + remaining[0] * share,
          c.out.f[1] + remaining[1] * share,
          c.out.f[2] + remaining[2] * share,
        ];
        if (c.out.kind === "ground") {
          clampGround(c);
          if (!(c.normalClamped || c.frictionClamped)) stillFree.push(i);
        } else {
          capPerson(c, capN);
          if (!c.forceCapped) stillFree.push(i);
        }
      }
      const applied: Vec3 = [0, 0, 0];
      for (const c of own) addTo(applied, c.out.f);
      remaining = sub(need, applied);
      free = stillFree;
    }
    const applied: Vec3 = [0, 0, 0];
    for (const c of own) addTo(applied, c.out.f);
    residualF.set(person.id, sub(need, applied));
    processed.add(person.id);

    for (const c of own) {
      if (c.frictionClamped) c.out.frictionClamped = true;
      if (c.forceCapped) c.out.forceCapped = true;
      person.external.push({ p: clone(c.out.p), f: clone(c.out.f), segment: c.segment });
      if (c.out.kind !== "person" || c.out.otherPersonId == null) continue;
      const other = byId.get(c.out.otherPersonId);
      if (!other) continue;
      const f = mul(c.out.f, -1);
      const load: ExternalLoad = { p: clone(c.out.p), f, segment: c.mirrorSegment };
      other.external.push(load);
      const list = received.get(other.id);
      if (list) list.push(load);
      else received.set(other.id, [load]);
      const mirror: BiomechContact = {
        personId: other.id,
        otherPersonId: person.id,
        joint: c.mirrorPoint ?? "mid_hip",
        kind: "person",
        p: clone(c.out.p),
        uv: c.mirrorUv,
        f: clone(f),
      };
      if (c.forceCapped) mirror.forceCapped = true;
      mirrored.push(mirror);
      if (processed.has(other.id)) {
        // Rare: the supporting person ranked above the supported one — keep the books
        // exact by folding the late load into its residual.
        const r = residualF.get(other.id);
        if (r) residualF.set(other.id, sub(r, f));
      }
    }
  }

  // Residual moment about COM: Ḣ minus the moment of all external forces.
  const residuals = new Map<number, { f: Vec3; m: Vec3 }>();
  for (const person of people) {
    const hDot: Vec3 = [0, 0, 0];
    for (const seg of person.segments.values()) {
      addTo(hDot, mul(seg.alpha, seg.inertia));
      addTo(hDot, cross(sub(seg.com, person.com), mul(seg.acc, seg.mass)));
    }
    const extMoment: Vec3 = [0, 0, 0];
    for (const ext of person.external) addTo(extMoment, cross(sub(ext.p, person.com), ext.f));
    residuals.set(person.id, {
      f: residualF.get(person.id) ?? [0, 0, 0],
      m: sub(hDot, extMoment),
    });
  }

  const out: BiomechContact[] = [];
  for (const person of people) {
    for (const c of contacts) if (c.out.personId === person.id) out.push(c.out);
    for (const c of mirrored) if (c.personId === person.id) out.push(c);
  }
  return { residuals, contacts: out };
}

/* ----------------------------------------------- Newton–Euler dynamics */

interface SolvedSegment {
  fProx: Vec3;
  mProx: Vec3;
  proxPoint: Vec3;
}

function inverseDynamics(person: PersonKin, g: Vec3): Partial<Record<JointId, BiomechJoint>> {
  const childrenOf = new Map<SegmentId, SegmentId[]>();
  for (const seg of person.segments.values()) {
    const parent = seg.def.parent;
    if (!parent) continue;
    if (!person.segments.has(parent)) continue;
    const list = childrenOf.get(parent) ?? [];
    list.push(seg.def.id);
    childrenOf.set(parent, list);
  }

  // Each external load acts on its contact segment (or, failing that, the nearest segment).
  const extBySegment = new Map<SegmentId, ExternalLoad[]>();
  for (const ext of person.external) {
    if (ext.segment && person.segments.has(ext.segment)) {
      const list = extBySegment.get(ext.segment) ?? [];
      list.push(ext);
      extBySegment.set(ext.segment, list);
      continue;
    }
    let bestId: SegmentId | null = null;
    let bestD = Infinity;
    for (const seg of person.segments.values()) {
      const d = pointSegmentDistance(ext.p, seg.prox, seg.distal);
      if (d < bestD) {
        bestD = d;
        bestId = seg.def.id;
      }
    }
    if (!bestId) continue;
    const list = extBySegment.get(bestId) ?? [];
    list.push(ext);
    extBySegment.set(bestId, list);
  }

  const joints: Partial<Record<JointId, BiomechJoint>> = {};

  const solve = (id: SegmentId): SolvedSegment | null => {
    const seg = person.segments.get(id);
    if (!seg) return null;

    const childSolutions: { sol: SolvedSegment }[] = [];
    for (const childId of childrenOf.get(id) ?? []) {
      const sol = solve(childId);
      if (sol) childSolutions.push({ sol });
    }

    const extLoads = extBySegment.get(id) ?? [];
    const extForce: Vec3 = [0, 0, 0];
    for (const e of extLoads) addTo(extForce, e.f);

    // F_prox = m(a − g) − F_ext + Σ F_prox(child)   [child applies −F_prox(child)]
    const fProx = sub(mul(sub(seg.acc, g), seg.mass), extForce);
    for (const c of childSolutions) addTo(fProx, c.sol.fProx);

    // M_prox = Iα − (p_prox − com)×F_prox
    //          + Σ[(p_childJoint − com)×F_prox(child) + M_prox(child)]
    //          − Σ (p_ext − com)×F_ext
    const mProx = mul(seg.alpha, seg.inertia);
    addTo(mProx, mul(cross(sub(seg.prox, seg.com), fProx), -1));
    for (const c of childSolutions) {
      addTo(mProx, cross(sub(c.sol.proxPoint, seg.com), c.sol.fProx));
      addTo(mProx, c.sol.mProx);
    }
    for (const e of extLoads) {
      addTo(mProx, mul(cross(sub(e.p, seg.com), e.f), -1));
    }

    const jointId = seg.def.joint;
    if (jointId) {
      const uv = uvOfEnd(person.uv, seg.def.proximal);
      joints[jointId] = {
        p: clone(seg.prox),
        uv,
        f: clone(fProx),
        m: clone(mProx),
        load: jointLoad(jointId, len(mProx), person.massKg),
      };
    }
    return { fProx, mProx, proxPoint: seg.prox };
  };

  solve("trunk");
  return joints;
}

/* --------------------------------------------------------- validation */

export interface ValidationInput {
  massKg: number;
  heightM?: number | null;
  id: number;
}

/** Contiguous true runs as [start, endInclusive]. */
function runsOf(mask: Uint8Array): [number, number][] {
  const out: [number, number][] = [];
  let start = -1;
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]) {
      if (start < 0) start = i;
    } else if (start >= 0) {
      out.push([start, i - 1]);
      start = -1;
    }
  }
  if (start >= 0) out.push([start, mask.length - 1]);
  return out;
}

/** `mask` without the first/last `n` samples of each true run. */
function trimEdges(mask: Uint8Array, n: number): Uint8Array {
  if (n <= 0) return mask.slice();
  const out = new Uint8Array(mask.length);
  for (const [s, e] of runsOf(mask)) {
    for (let i = s + n; i <= e - n; i++) out[i] = 1;
  }
  return out;
}

/**
 * Centred moving average of a (T×3, flat) vector series over valid samples, then the norm
 * divided by `norm`; NaN where fewer than ceil(width/2) window samples are valid.
 */
function sustained(vec: Float64Array, valid: Uint8Array, width: number, norm: number): Float64Array {
  const n = valid.length;
  const out = new Float64Array(n).fill(NaN);
  const cx = new Float64Array(n + 1);
  const cy = new Float64Array(n + 1);
  const cz = new Float64Array(n + 1);
  const cc = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) {
    const ok = valid[i] ? 1 : 0;
    cx[i + 1] = cx[i]! + (ok ? vec[3 * i]! : 0);
    cy[i + 1] = cy[i]! + (ok ? vec[3 * i + 1]! : 0);
    cz[i + 1] = cz[i]! + (ok ? vec[3 * i + 2]! : 0);
    cc[i + 1] = cc[i]! + ok;
  }
  const half = Math.floor(width / 2);
  const need = Math.max(1, Math.ceil(width / 2));
  for (let i = 0; i < n; i++) {
    if (!valid[i]) continue;
    const lo = Math.max(0, i - half);
    const hi = Math.min(n, i + half + 1);
    const cnt = cc[hi]! - cc[lo]!;
    if (cnt < need) continue;
    out[i] =
      Math.hypot(
        (cx[hi]! - cx[lo]!) / cnt,
        (cy[hi]! - cy[lo]!) / cnt,
        (cz[hi]! - cz[lo]!) / cnt,
      ) / norm;
  }
  return out;
}

/** numpy-style (linear) 90th percentile; 0 when empty. */
function p90(values: number[]): number {
  if (values.length === 0) return 0;
  const v = values.slice().sort((a, b) => a - b);
  const pos = 0.9 * (v.length - 1);
  const lo = Math.floor(pos);
  const hi = Math.min(v.length - 1, lo + 1);
  return v[lo]! + (v[hi]! - v[lo]!) * (pos - lo);
}

function medianOf(values: number[]): number {
  if (values.length === 0) return NaN;
  const v = values.slice().sort((a, b) => a - b);
  const m = v.length >> 1;
  return v.length % 2 ? v[m]! : (v[m - 1]! + v[m]!) / 2;
}

const round4 = (x: number): number => Math.round(x * 1e4) / 1e4;

/**
 * ValidationSummary over a window of solved frames — the streaming twin of
 * acro_movement/biomech/validation.py (same thresholds, penalties, findings and metrics).
 * `persons` supplies mass/height (BW and BW·h normalisers).
 *
 * Intentional differences from the offline validator (live overlay constraints):
 * - The input is a rolling window (the last ~3 s), so the window's own ends count as run
 *   edges and are trimmed like real ones (conservative: a few fewer scored frames).
 * - A window shorter than 2·EDGE_FRAMES + 1 frames (a paused, quasi-static snapshot, which
 *   has zero accelerations and hence no derivative edge effects) is scored without edge
 *   trimming, and the sustain window shrinks to the frames available. Python would report
 *   insufficient-data for such a short track.
 * - |a_com − g| is taken from the residual (= m·(a − g) when a person has no contacts), since
 *   frames do not carry the COM acceleration; it is only used on contact-free frames.
 * - Floor penetration checks the solved joint positions (frames carry no raw landmarks).
 * - Friction / cap violations come from the realtime `frictionClamped` / `forceCapped`
 *   contact flags; frames without flags (e.g. an offline track) report none.
 * - fps is taken from the median frame spacing unless `opts.fps` is given.
 */
export function validateFrames(
  frames: BiomechFrame[],
  persons: ValidationInput[],
  opts?: { method?: "opensim" | "estimate" | "mujoco"; floorY?: number; coverage?: number; fps?: number },
): ValidationSummary {
  const method = opts?.method ?? "estimate";
  const floorY = opts?.floorY ?? 0;
  const G = 9.81;
  const n = frames.length;

  // Person index: declared persons first, then any id that only appears in frames.
  const ids: number[] = [];
  const massById = new Map<number, number>();
  const heightById = new Map<number, number>();
  for (const p of persons) {
    if (massById.has(p.id)) continue;
    ids.push(p.id);
    massById.set(p.id, p.massKg || 70);
    heightById.set(p.id, Math.max(p.heightM || 1.72, 0.5));
  }
  for (const f of frames) {
    for (const p of f.persons) {
      if (massById.has(p.id)) continue;
      ids.push(p.id);
      massById.set(p.id, 70);
      heightById.set(p.id, 1.72);
    }
  }
  const nP = ids.length;
  const index = new Map(ids.map((id, i) => [id, i]));

  // fps from the median spacing (rolling windows may have jitter / dropped frames).
  let fps = opts?.fps ?? 0;
  if (!(fps > 0)) {
    const dts: number[] = [];
    for (let i = 1; i < n; i++) {
      const dt = frames[i]!.t - frames[i - 1]!.t;
      if (dt > 1e-6) dts.push(dt);
    }
    const md = medianOf(dts);
    fps = Number.isFinite(md) && md > 0 ? 1 / md : 30;
  }
  const edges = n >= 2 * EDGE_FRAMES + 1 ? EDGE_FRAMES : 0;
  let width = Math.max(1, Math.round(SUSTAIN_WINDOW_S * fps));
  if (width % 2 === 0) width += 1;
  if (width > n) width = Math.max(1, n % 2 === 1 ? n : n - 1);

  // --- per person / frame series
  const valid: Uint8Array[] = [];
  const validM: Uint8Array[] = [];
  const hasContact: Uint8Array[] = [];
  const capped: Uint8Array[] = [];
  const penetration: Uint8Array[] = [];
  const dev: Float64Array[] = [];
  const resF: Float64Array[] = [];
  const resM: Float64Array[] = [];
  const loads: Map<string, Float64Array>[] = [];
  for (let pi = 0; pi < nP; pi++) {
    valid.push(new Uint8Array(n));
    validM.push(new Uint8Array(n));
    hasContact.push(new Uint8Array(n));
    capped.push(new Uint8Array(n));
    penetration.push(new Uint8Array(n));
    dev.push(new Float64Array(n).fill(NaN));
    resF.push(new Float64Array(3 * n));
    resM.push(new Float64Array(3 * n));
    loads.push(new Map());
  }
  const friction = new Uint8Array(n);
  const finite3 = (v: Vec3): boolean =>
    Number.isFinite(v[0]) && Number.isFinite(v[1]) && Number.isFinite(v[2]);

  for (let t = 0; t < n; t++) {
    const frame = frames[t]!;
    for (const c of frame.contacts) {
      const pi = index.get(c.personId);
      if (pi != null) {
        hasContact[pi]![t] = 1;
        if (c.kind === "person" && c.forceCapped) capped[pi]![t] = 1;
      }
      if (c.kind === "ground" && c.frictionClamped) friction[t] = 1;
    }
    for (const person of frame.persons) {
      const pi = index.get(person.id);
      if (pi == null) continue;
      const rf = person.residual?.f;
      const rm = person.residual?.m;
      if (rf && finite3(rf)) {
        valid[pi]![t] = 1;
        resF[pi]!.set(rf, 3 * t);
        if (!hasContact[pi]![t]) dev[pi]![t] = len(rf) / massById.get(person.id)!;
        if (rm && finite3(rm)) {
          validM[pi]![t] = 1;
          resM[pi]!.set(rm, 3 * t);
        }
      }
      for (const [jointId, joint] of Object.entries(person.joints)) {
        if (!joint) continue;
        let series = loads[pi]!.get(jointId);
        if (!series) {
          series = new Float64Array(n).fill(NaN);
          loads[pi]!.set(jointId, series);
        }
        series[t] = joint.load;
        if (joint.p[1] < floorY - FLOOR_PENETRATION_M) penetration[pi]![t] = 1;
      }
    }
  }

  // --- scored frames
  const scored: Uint8Array[] = [];
  const airborneCore: Uint8Array[] = [];
  const scoredAny = new Uint8Array(n);
  for (let pi = 0; pi < nP; pi++) {
    const s = trimEdges(valid[pi]!, edges);
    scored.push(s);
    const free = new Uint8Array(n);
    for (let t = 0; t < n; t++) free[t] = valid[pi]![t] && !hasContact[pi]![t] ? 1 : 0;
    airborneCore.push(trimEdges(free, edges));
    for (let t = 0; t < n; t++) if (s[t]) scoredAny[t] = 1;
  }
  let nScored = 0;
  for (let t = 0; t < n; t++) nScored += scoredAny[t]!;
  const denom = Math.max(nScored, 1);

  // --- residuals
  const susF: Float64Array[] = [];
  const susM: Float64Array[] = [];
  const supF: number[] = [];
  const supM: number[] = [];
  let maxResF = 0;
  let maxResM = 0;
  let rmsSum = 0;
  let rmsN = 0;
  for (let pi = 0; pi < nP; pi++) {
    const id = ids[pi]!;
    const bw = massById.get(id)! * G;
    const bwh = bw * heightById.get(id)!;
    const sf = sustained(resF[pi]!, valid[pi]!, width, bw);
    const sm = sustained(resM[pi]!, validM[pi]!, width, bwh);
    susF.push(sf);
    susM.push(sm);
    for (let t = 0; t < n; t++) {
      if (valid[pi]![t]) {
        const r = resF[pi]!;
        const f = Math.hypot(r[3 * t]!, r[3 * t + 1]!, r[3 * t + 2]!) / bw;
        maxResF = Math.max(maxResF, f);
        if (scored[pi]![t] && hasContact[pi]![t]) {
          rmsSum += f * f;
          rmsN++;
        }
      }
      if (validM[pi]![t]) {
        const r = resM[pi]!;
        maxResM = Math.max(maxResM, Math.hypot(r[3 * t]!, r[3 * t + 1]!, r[3 * t + 2]!) / bwh);
      }
      if (scored[pi]![t] && hasContact[pi]![t]) {
        if (Number.isFinite(sf[t]!)) supF.push(sf[t]!);
        if (Number.isFinite(sm[t]!)) supM.push(sm[t]!);
      }
    }
  }
  const p90F = p90(supF);
  const p90M = p90(supM);
  const rmsResF = rmsN > 0 ? Math.sqrt(rmsSum / rmsN) : 0;

  // --- joint loads over scored frames
  let maxLoad = 0;
  const overload = new Uint8Array(n);
  for (let pi = 0; pi < nP; pi++) {
    for (const series of loads[pi]!.values()) {
      for (let t = 0; t < n; t++) {
        const v = series[t]!;
        if (!scored[pi]![t] || !Number.isFinite(v)) continue;
        if (v > maxLoad) maxLoad = v;
        if (v > 1) overload[t] = 1;
      }
    }
  }

  // --- frame counts
  let overloadFrames = 0;
  let airborneFrames = 0;
  let frictionFrames = 0;
  let cappedFrames = 0;
  let floorFrames = 0;
  const airborneBad: Uint8Array[] = [];
  for (let pi = 0; pi < nP; pi++) {
    const bad = new Uint8Array(n);
    for (let t = 0; t < n; t++) {
      if (airborneCore[pi]![t] && dev[pi]![t]! > AIRBORNE_TOLERANCE) bad[t] = 1;
    }
    airborneBad.push(bad);
  }
  for (let t = 0; t < n; t++) {
    if (overload[t]) overloadFrames++;
    if (friction[t] && scoredAny[t]) frictionFrames++;
    let air = false;
    let cap = false;
    let pen = false;
    for (let pi = 0; pi < nP; pi++) {
      if (airborneBad[pi]![t]) air = true;
      if (capped[pi]![t] && scored[pi]![t]) cap = true;
      if (penetration[pi]![t]) pen = true;
    }
    if (air) airborneFrames++;
    if (cap) cappedFrames++;
    if (pen) floorFrames++;
  }

  // --- findings
  const times = frames.map((f) => f.t);
  const findings: ValidationFinding[] = [];
  const finding = (
    code: ValidationFinding["code"],
    severity: ValidationFinding["severity"],
    message: string,
    start: number,
    end: number,
    extra?: { personId?: number | null; joint?: string | null; value?: number | null },
  ): void => {
    const value = extra?.value;
    findings.push({
      code,
      severity,
      message,
      personId: extra?.personId ?? null,
      joint: extra?.joint ?? null,
      startTime: n ? round4(times[start]!) : 0,
      endTime: n ? round4(times[end]!) : 0,
      value: value != null && Number.isFinite(value) ? round4(value) : null,
    });
  };
  const peakOf = (col: Float64Array, s: number, e: number): number => {
    let peak = -Infinity;
    for (let i = s; i <= e; i++) if (Number.isFinite(col[i]!)) peak = Math.max(peak, col[i]!);
    return peak;
  };

  for (let pi = 0; pi < nP; pi++) {
    const id = ids[pi]!;
    const col = susF[pi]!;
    const bad = new Uint8Array(n);
    for (let t = 0; t < n; t++) bad[t] = scored[pi]![t] && col[t]! > RESIDUAL_FINDING_BW ? 1 : 0;
    for (const [s, e] of runsOf(bad)) {
      const peak = peakOf(col, s, e);
      finding(
        "residual-high",
        peak > RESIDUAL_ERROR_BW ? "error" : "warn",
        `Unexplained external force up to ${peak.toFixed(2)} body weights for person ${id}.`,
        s,
        e,
        { personId: id, value: peak },
      );
    }
  }
  for (let pi = 0; pi < nP; pi++) {
    const id = ids[pi]!;
    const col = susM[pi]!;
    const bad = new Uint8Array(n);
    for (let t = 0; t < n; t++) {
      bad[t] = scored[pi]![t] && hasContact[pi]![t] && col[t]! > RESIDUAL_MOMENT_FINDING_BWH ? 1 : 0;
    }
    for (const [s, e] of runsOf(bad)) {
      const peak = peakOf(col, s, e);
      finding(
        "residual-moment-high",
        peak > RESIDUAL_MOMENT_ERROR_BWH ? "error" : "warn",
        `Person ${id}'s balance cannot be explained by the contacts (unexplained moment ${peak.toFixed(2)} BW x height).`,
        s,
        e,
        { personId: id, value: peak },
      );
    }
  }
  for (const joint of JOINTS) {
    for (let pi = 0; pi < nP; pi++) {
      const series = loads[pi]!.get(joint);
      if (!series) continue;
      const col = new Float64Array(n).fill(NaN);
      const bad = new Uint8Array(n);
      for (let t = 0; t < n; t++) {
        if (!scored[pi]![t]) continue;
        col[t] = series[t]!;
        if (series[t]! > 1) bad[t] = 1;
      }
      for (const [s, e] of runsOf(bad)) {
        const peak = peakOf(col, s, e);
        finding(
          "joint-overload",
          peak > 1.5 ? "error" : "warn",
          `${joint} moment reaches ${peak.toFixed(2)}x the modelled capacity for person ${ids[pi]}.`,
          s,
          e,
          { personId: ids[pi]!, joint, value: peak },
        );
      }
    }
  }
  for (let pi = 0; pi < nP; pi++) {
    const id = ids[pi]!;
    const col = dev[pi]!;
    for (const [s, e] of runsOf(airborneBad[pi]!)) {
      const peak = peakOf(col, s, e);
      const vals: number[] = [];
      for (let i = s; i <= e; i++) if (Number.isFinite(col[i]!)) vals.push(col[i]!);
      const longRun = (e - s + 1) / Math.max(fps, 1e-6) >= 0.25;
      finding(
        "airborne-inconsistent",
        longRun && medianOf(vals) > 0.5 * G ? "error" : "warn",
        `Person ${id} has no contacts but is not in free fall (|a - g| up to ${peak.toFixed(1)} m/s^2).`,
        s,
        e,
        { personId: id, value: peak },
      );
    }
  }
  for (let pi = 0; pi < nP; pi++) {
    const id = ids[pi]!;
    const mask = new Uint8Array(n);
    for (let t = 0; t < n; t++) mask[t] = capped[pi]![t] && scored[pi]![t] ? 1 : 0;
    for (const [s, e] of runsOf(mask)) {
      finding(
        "contact-force-capped",
        "warn",
        `A person contact on person ${id} would need more than 3 body weights; the excess is reported as residual.`,
        s,
        e,
        { personId: id },
      );
    }
  }
  {
    const mask = new Uint8Array(n);
    for (let t = 0; t < n; t++) mask[t] = friction[t] && scoredAny[t] ? 1 : 0;
    for (const [s, e] of runsOf(mask)) {
      finding("friction-violation", "warn", "Ground contact requires more friction than mu = 0.8 allows.", s, e);
    }
  }
  for (let pi = 0; pi < nP; pi++) {
    const id = ids[pi]!;
    for (const [s, e] of runsOf(penetration[pi]!)) {
      finding(
        "floor-penetration",
        "warn",
        `Person ${id} has landmarks more than ${(FLOOR_PENETRATION_M * 100).toFixed(0)} cm below the floor.`,
        s,
        e,
        { personId: id },
      );
    }
  }
  const lastIndex = Math.max(n - 1, 0);
  const lowConfidence = opts?.coverage != null && opts.coverage < LOW_CONFIDENCE;
  if (lowConfidence) {
    finding(
      "low-confidence-pose",
      "error",
      `Only ${(opts!.coverage! * 100).toFixed(0)}% of the required landmarks are visible; the analysis is not trustworthy.`,
      0,
      lastIndex,
      { value: opts!.coverage! },
    );
  }
  const insufficient = nScored === 0;
  if (insufficient) {
    finding(
      "insufficient-data",
      "error",
      `Too few consecutive valid frames to check the physics (needs at least ${2 * EDGE_FRAMES + 1} after filtering).`,
      0,
      lastIndex,
    );
  }

  const rank: Record<string, number> = { error: 0, warn: 1, info: 2 };
  findings.sort(
    (a, b) =>
      (rank[a.severity] ?? 3) - (rank[b.severity] ?? 3) ||
      (b.value ?? 0) - (a.value ?? 0) ||
      a.startTime - b.startTime,
  );
  findings.length = Math.min(findings.length, MAX_FINDINGS);

  // --- score
  let score = 100;
  score -= RESIDUAL_FORCE_PENALTY * Math.max(0, p90F - RESIDUAL_FORCE_FREE_BW);
  score -= RESIDUAL_MOMENT_PENALTY * Math.max(0, p90M - RESIDUAL_MOMENT_FREE_BWH);
  score -= OVERLOAD_PENALTY * (overloadFrames / denom);
  score -= AIRBORNE_PENALTY * (airborneFrames / denom);
  score -= FRICTION_PENALTY * (frictionFrames / denom);
  score -= PENETRATION_PENALTY * (floorFrames / Math.max(n, 1));
  score -= CONTACT_CAP_PENALTY * (cappedFrames / denom);
  if (lowConfidence || insufficient) score = Math.min(score, LOW_CONFIDENCE_SCORE);
  score = Math.min(100, Math.max(0, score));

  return {
    status: score >= PASS_SCORE ? "pass" : score >= WARN_SCORE ? "warn" : "fail",
    score: Math.round(score * 10) / 10,
    method,
    metrics: {
      frameCount: n,
      maxResidualForceBw: round4(maxResF),
      rmsResidualForceBw: round4(rmsResF),
      maxResidualMomentBwH: round4(maxResM),
      maxLoad: round4(maxLoad),
      overloadFrames,
      airborneInconsistentFrames: airborneFrames,
      frictionViolationFrames: frictionFrames,
      floorPenetrationFrames: floorFrames,
      scoredFrames: nScored,
      sustainedResidualForceBw: round4(p90F),
      sustainedResidualMomentBwH: round4(p90M),
      contactForceCappedFrames: cappedFrames,
    },
    findings,
  };
}

export { JOINTS };
